/**
 * Evaluates carousel slide visibility from schedule strings entered in store timezone.
 * Schedule format: YYYY-MM-DD HH:MM
 */
class SlideSchedule {
	constructor(offset) {
		this.offset = SlideSchedule.normalizeOffset(offset);
	}

	static normalizeOffset(offset) {
		if (!offset) return '+00:00';

		const withColon = String(offset).trim().match(/^([+-])(\d{2}):(\d{2})$/);
		if (withColon) return `${withColon[1]}${withColon[2]}:${withColon[3]}`;

		const withoutColon = String(offset).trim().match(/^([+-])(\d{2})(\d{2})$/);
		if (withoutColon) return `${withoutColon[1]}${withoutColon[2]}:${withoutColon[3]}`;

		return '+00:00';
	}

	isVisible(displayStart, displayEnd) {
		const now = Date.now();

		if (displayStart) {
			const startMs = this.parseStoreDateTime(displayStart);
			if (startMs !== null && now < startMs) return false;
		}

		if (displayEnd) {
			const endMs = this.parseStoreDateTime(displayEnd);
			if (endMs !== null && now > endMs) return false;
		}

		return true;
	}

	parseStoreDateTime(dateTimeStr) {
		const match = String(dateTimeStr)
			.trim()
			.match(/^(\d{4}-\d{2}-\d{2})(?:\s+(\d{1,2}):(\d{2}))?$/);
		if (!match) return null;

		const hour = String(match[2] ?? 0).padStart(2, '0');
		const minute = match[3] ?? '00';
		const iso = `${match[1]}T${hour}:${minute}:00${this.offset}`;
		const ms = Date.parse(iso);

		return Number.isNaN(ms) ? null : ms;
	}
}

function getStoreOffset(root) {
	return SlideSchedule.normalizeOffset(
		root?.dataset?.storeTimezoneOffset || window.Shopify?.storeTimezoneOffset
	);
}

function filterScheduledSlides(root) {
	const schedule = new SlideSchedule(getStoreOffset(root));
	let removed = 0;

	root.querySelectorAll('swiper-slide').forEach((slide) => {
		const { displayStart, displayEnd } = slide.dataset;
		if (!displayStart && !displayEnd) return;

		if (!schedule.isVisible(displayStart, displayEnd)) {
			slide.remove();
			removed++;
		}
	});

	return {
		removed,
		remaining: root.querySelectorAll('swiper-slide').length,
	};
}

function hideScheduledCarousel(root) {
	root.hidden = true;
	root.style.display = 'none';
}

function updateSwiperAfterFilter(root) {
	const swiperElements = root.querySelectorAll('swiper-container');

	swiperElements.forEach((swiperEl) => {
		const remaining = swiperEl.querySelectorAll('swiper-slide').length;

		if (remaining === 0) return;

		if (remaining === 1) {
			swiperEl.setAttribute('loop', 'false');
		}

		if (swiperEl.swiper) {
			if (remaining === 1) {
				swiperEl.swiper.loop = false;
			}
			swiperEl.swiper.update();
		}
	});
}

function initializeScheduledSwipers(root) {
	root.querySelectorAll('swiper-container[init="false"]').forEach((swiperEl) => {
		if (!swiperEl.swiper && typeof swiperEl.initialize === 'function') {
			swiperEl.initialize();
		}
	});
}

function initScheduledCarousel(root) {
	// When there are no carousel slides (e.g. a single static hero), there is
	// nothing to schedule or slide. Bail out early so the section is not hidden.
	if (root.querySelectorAll('swiper-slide').length === 0) {
		document.dispatchEvent(
			new CustomEvent('scheduled-carousel:ready', {
				detail: { root },
			})
		);
		return;
	}

	const { remaining } = filterScheduledSlides(root);

	if (remaining === 0) {
		hideScheduledCarousel(root);
		return;
	}

	updateSwiperAfterFilter(root);
	initializeScheduledSwipers(root);

	document.dispatchEvent(
		new CustomEvent('scheduled-carousel:ready', {
			detail: { root },
		})
	);
}

function initAllScheduledCarousels() {
	document.querySelectorAll('[data-scheduled-carousel]').forEach(initScheduledCarousel);
}

function bootScheduledCarousels() {
	if (customElements.get('swiper-container')) {
		initAllScheduledCarousels();
		return;
	}

	customElements.whenDefined('swiper-container').then(initAllScheduledCarousels);
}

if (!window.__slideScheduleBooted) {
	window.__slideScheduleBooted = true;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', bootScheduledCarousels);
	} else {
		bootScheduledCarousels();
	}
}
